import { defineComponent, h, ref, type Component, type Ref } from 'vue';
import { template as compileLodashTemplate } from 'lodash-es';
import type { FormatterFactory, GridFormatter } from '../types';
import { useGridI18n } from '../gridI18n';

export interface TemplateOptions {
  format: string;
  engine?: string;
}

const interpolate = /{{([\s\S]+?)}}/g;

export interface TemplateFormatters {
  number(
    value: any,
    opts?: { decimals?: number; minDecimals?: number },
  ): string;
  date(value: any): string;
}

type RenderHtml = (value: any, context: any, fmt: TemplateFormatters) => string;

const detachedFormatters: TemplateFormatters = {
  number: (value) =>
    value == null || !Number.isFinite(Number(value))
      ? (value ?? '')
      : Number(value).toLocaleString(),
  date: (value) => (value == null ? '' : String(value)),
};

let handlebarsModule: Promise<any> | null = null;
function loadHandlebars(): Promise<any> {
  if (!handlebarsModule) {
    handlebarsModule = import('handlebars').then((mod) => mod.default ?? mod);
  }
  return handlebarsModule;
}

function normalizeFormat(value: string): string {
  return value.startsWith('#!') ? value.slice(2) : value;
}

function stripHtmlTags(input: string): string {
  return input
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const template: FormatterFactory = (options: TemplateOptions) => {
  const format = options?.format ?? '';
  return options?.engine === 'hb'
    ? handlebarsFormatter(format)
    : lodashFormatter(format);
};

function lodashFormatter(format: string): GridFormatter {
  const compiled = compileLodashTemplate(format, { interpolate });
  const render: RenderHtml = (value, context, fmt) =>
    context ? compiled({ $value: value, $fmt: fmt, ...context }) : '';
  return htmlFormatter(render);
}

function handlebarsFormatter(format: string): GridFormatter {
  const ready = ref(false);
  let compiled: ((data: any) => string) | null = null;

  loadHandlebars()
    .then((Handlebars) => {
      compiled = Handlebars.compile(normalizeFormat(format));
    })
    .catch((error) => {
      compiled = () => `#ERROR ${error}`;
    })
    .finally(() => {
      ready.value = true;
    });

  const render: RenderHtml = (value, context, fmt) => {
    if (!context || !compiled) {
      return '';
    }
    try {
      return compiled({ $value: value, $fmt: fmt, ...context });
    } catch (error) {
      return `#ERROR ${error}`;
    }
  };

  return htmlFormatter(render, ready);
}

function htmlFormatter(
  render: RenderHtml,
  ready?: Ref<boolean>,
): GridFormatter {
  const component = defineComponent({
    props: {
      value: { type: null, default: undefined },
      context: { type: null, default: undefined },
    },
    setup(props) {
      const { formatNumber, formatDate } = useGridI18n();
      const fmt: TemplateFormatters = {
        number: formatNumber,
        date: formatDate,
      };
      return () => {
        const loaded = ready ? ready.value : true;
        return h('span', {
          class: 'itp-data-grid__template',
          innerHTML: loaded ? render(props.value, props.context, fmt) : '',
        });
      };
    },
  });

  return {
    component: component as Component,
    props: (_value, context) => ({ context }),
    toText: (value, context) =>
      stripHtmlTags(render(value, context, detachedFormatters)),
  };
}
